import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { projectAPI, documentAPI, taskAPI, customerAPI, userAPI, crewsAPI } from '../../services/api';
import TaskModal from '../TaskModal/TaskModal';
import ProjectModal from '../ProjectModal/ProjectModal';
import DetailsDrawer from './DetailsDrawer';
import ActivityTab from './tabs/ActivityTab';
import TasksTab from './tabs/TasksTab';
import PhotosTab from './tabs/PhotosTab';
import DocumentsTab from './tabs/DocumentsTab';
import WorkOrdersTab from './tabs/WorkOrdersTab';
import ProjectContacts from './ProjectContacts';
import EmailComposer from './EmailComposer';
import EmailHistory from './EmailHistory';
import { useAuth } from '../../AuthContext';
import {
    STATUS_SELECT_OPTIONS,
    STATUS_LABEL_BY_INDEX,
    STATUS_KEY_BY_INDEX,
} from '../../constants/projectStatus';
import { contactDisplayName } from '../../utils/contactName';
import './projectDetail.css';
import './projectDetailTabs.css';
import './emailComposer.css';
import './emailHistory.css';

const ProjectDetail = ({ projectId, onBack }) => {
    const { isAdmin } = useAuth();
    const [project, setProject] = useState(null);
    const [documents, setDocuments] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [activeTab, setActiveTab] = useState('activity');
    const [workOrderCount, setWorkOrderCount] = useState(undefined);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [docRefreshTrigger, setDocRefreshTrigger] = useState(0);
    const [showTaskModal, setShowTaskModal] = useState(false);
    const [editingTask, setEditingTask] = useState(null);
    const [showProjectModal, setShowProjectModal] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [users, setUsers] = useState([]);
    const [crews, setCrews] = useState([]);
    const [emailRefreshTrigger, setEmailRefreshTrigger] = useState(0);
    const [refireBusy, setRefireBusy] = useState(false);
    const [statusBusy, setStatusBusy] = useState(false);
    // Inline-save failures show as a banner. Kept apart from `error`, which
    // replaces the whole page — a failed field edit shouldn't blank the job.
    const [saveError, setSaveError] = useState('');

    const handleReFireAutomations = async () => {
        if (!project) return;
        const ok = window.confirm(
            `Re-fire status automations for "${project.projectName}" (status: ${STATUS_LABEL_BY_INDEX[project.status] || 'Unknown'})?\n\n` +
            `This evaluates all status-triggered automations against the project's current state and runs the ones that match. ` +
            `It can create tasks, send emails, and change status — same as the original trigger would have.`
        );
        if (!ok) return;
        setRefireBusy(true);
        try {
            const res = await projectAPI.refireAutomations(project.id);
            const fired = res.data?.fired ?? 0;
            alert(fired === 0
                ? 'No automations matched the current state. Check that the project has the right context filled in.'
                : `Re-fired ${fired} automation${fired === 1 ? '' : 's'}.`);
            // Refresh project + related data so any side effects show up.
            fetchProject();
        } catch (err) {
            console.error('Re-fire failed:', err);
            alert(err.response?.data?.message || 'Failed to re-fire automations.');
        } finally {
            setRefireBusy(false);
        }
    };

    const fetchProject = useCallback(async () => {
        const response = await projectAPI.getById(projectId);
        setProject(response.data);
    }, [projectId]);

    const fetchDocuments = useCallback(async () => {
        const response = await documentAPI.getByProject(projectId);
        setDocuments(response.data);
    }, [projectId]);

    const fetchTasks = useCallback(async () => {
        const response = await taskAPI.getByProject(projectId);
        setTasks(response.data);
    }, [projectId]);

    useEffect(() => {
        const loadAll = async () => {
            try {
                setLoading(true);
                await Promise.all([fetchProject(), fetchDocuments(), fetchTasks()]);
            } catch (err) {
                setError('Failed to load project');
                console.error('Error loading project:', err);
            } finally {
                setLoading(false);
            }
        };

        if (projectId) loadAll();
    }, [projectId, fetchProject, fetchDocuments, fetchTasks]);

    useEffect(() => {
        const loadLookups = async () => {
            try {
                const [custRes, userRes, crewRes] = await Promise.all([
                    // PMs own jobs too, so they belong in the Edit Project picker
                    // and in the drawer's read-back of who the job is for.
                    customerAPI.getAll({ type: 'Homeowner,PropertyManager', limit: 1000 }),
                    userAPI.getAll(),
                    // Needed by the Current Installer dropdown, which offers crew names
                    // and crew leads alongside people.
                    crewsAPI.getAll().catch(() => ({ data: [] })),
                ]);
                setCustomers(custRes.data);
                setUsers(userRes.data);
                setCrews(crewRes.data || []);
            } catch (err) {
                console.error('Error loading lookups:', err);
            }
        };
        loadLookups();
    }, []);

    // Re-fetch docs when trigger changes (after upload/delete)
    useEffect(() => {
        if (docRefreshTrigger > 0) fetchDocuments();
    }, [docRefreshTrigger, fetchDocuments]);

    const handleDocRefresh = useCallback(() => {
        setDocRefreshTrigger(prev => prev + 1);
    }, []);

    const handleTaskRefresh = useCallback(() => {
        fetchTasks();
        fetchProject();
    }, [fetchTasks, fetchProject]);

    // Status goes through the dedicated PATCH rather than a full save: it's the
    // same path the job board uses, so it stamps StatusChangedAt and fires the
    // status automations exactly like dragging a card does.
    const handleStatusChange = useCallback(async (nextIndex) => {
        if (!project || nextIndex === project.status) return;
        const key = STATUS_KEY_BY_INDEX[nextIndex];
        if (!key) return;

        setStatusBusy(true);
        setSaveError('');
        try {
            await projectAPI.updateStatus(project.id, key);
            // Automations can create tasks and edit the job, so reload both
            // rather than patching the status in local state.
            await Promise.all([fetchProject(), fetchTasks()]);
        } catch (err) {
            console.error('Status change failed:', err);
            setSaveError(err.response?.data?.message || 'Could not change the status.');
        } finally {
            setStatusBusy(false);
        }
    }, [project, fetchProject, fetchTasks]);

    // One field at a time, straight from the details drawer. PUT replaces the
    // whole row, so the loaded project is sent back with the single field
    // changed; the API ignores navigation properties and owns StatusChangedAt.
    // Re-throwing lets the field put back the value that didn't save.
    const handleFieldSave = useCallback(async (field, value) => {
        if (!project) return;
        setSaveError('');
        try {
            // An object means several fields at once. The installer picker needs it:
            // it sets one of scheduledCrewId / scheduledInstallerId and clears the
            // other, and the API rejects a job holding both — so saving them
            // separately would pass through a state the server refuses.
            const patch = (field && typeof field === 'object') ? field : { [field]: value };
            await projectAPI.update(project.id, { ...project, ...patch });
            // A save can auto-advance the status (filling in a key date does),
            // which in turn fires automations — so reload rather than assume.
            await Promise.all([fetchProject(), fetchTasks()]);
        } catch (err) {
            console.error('Failed to save project field:', err);
            setSaveError(err.response?.data?.message || 'Could not save that change.');
            throw err;
        }
    }, [project, fetchProject, fetchTasks]);

    // Derive photo vs non-photo documents
    const photos = useMemo(() => documents.filter(d => d.documentType === 0), [documents]);
    const nonPhotoDocs = useMemo(() => documents.filter(d => d.documentType !== 0), [documents]);

    const activeTasks = useMemo(() => tasks.filter(t => t.status !== 2), [tasks]);

    const taskDefaultValues = useMemo(() => ({
        projectId: projectId,
        customerId: project?.customerId || ''
    }), [projectId, project?.customerId]);

    if (loading) return <div className="loading">Loading project...</div>;
    if (error) return (
        <div className="project-detail-page">
            <div className="error-message">{error}</div>
            <button className="btn-back" onClick={onBack}>Back to Projects</button>
        </div>
    );
    if (!project) return null;

    const customerName = project.customer
        ? contactDisplayName(project.customer)
        : 'Unknown Customer';

    const statusText = STATUS_LABEL_BY_INDEX[project.status] || 'Unknown';

    const tabs = [
        { id: 'activity', label: 'Activity', icon: 'fa-stream' },
        { id: 'tasks', label: 'Tasks', icon: 'fa-tasks', count: activeTasks.length },
        { id: 'photos', label: 'Photos', icon: 'fa-camera', count: photos.length },
        { id: 'documents', label: 'Documents', icon: 'fa-file-alt', count: nonPhotoDocs.length },
        { id: 'workorders', label: 'Work Orders', icon: 'fa-clipboard-list', count: workOrderCount },
        { id: 'contacts', label: 'Contacts', icon: 'fa-address-book' },
        { id: 'email', label: 'Email', icon: 'fa-envelope' },
    ];

    return (
        <div className="project-detail-page">
            <button className="btn-back" onClick={onBack}>
                <i className="fas fa-arrow-left"></i> Back to Projects
            </button>

            <div className="project-detail-header">
                <div className="project-detail-info">
                    <h1 className="project-detail-name">
                        {project.projectName}
                        {project.poNumber && (
                            <span style={{ marginLeft: '12px', fontFamily: 'monospace', fontSize: '0.7em', color: 'var(--text-muted, #57606a)', fontWeight: 'normal' }}>
                                {project.poNumber}
                            </span>
                        )}
                    </h1>
                    <p className="project-detail-customer">{customerName}</p>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {/* The badge itself is the control — changing the status is the
                        most common edit on a job and shouldn't need the modal. */}
                    <div className="pd-status-picker">
                        <select
                            className={`pd-status-select status-badge status-${statusText.toLowerCase().replace(/\s+/g, '-')}`}
                            value={project.status}
                            onChange={(e) => handleStatusChange(Number(e.target.value))}
                            disabled={statusBusy}
                            title="Change status — fires the same automations as moving the card on the board"
                        >
                            {STATUS_SELECT_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                            {/* A status the option list doesn't know about would otherwise
                                render as the first entry, silently misreporting the job. */}
                            {!STATUS_SELECT_OPTIONS.some(o => o.value === project.status) && (
                                <option value={project.status}>{statusText}</option>
                            )}
                        </select>
                        <i className={`fas ${statusBusy ? 'fa-spinner fa-spin' : 'fa-chevron-down'} pd-status-caret`}></i>
                    </div>
                    {isAdmin() && (
                        <button
                            className="btn-refire-automations"
                            onClick={handleReFireAutomations}
                            disabled={refireBusy}
                            title="Re-fire status-triggered automations against the project's current state. Use when the status was set before the right context (sales rep, shingle color, etc.) was filled in."
                            style={{
                                padding: '0.4rem 0.9rem',
                                background: refireBusy ? '#9ca3af' : 'transparent',
                                color: refireBusy ? '#fff' : 'var(--text-primary, #1a1a1a)',
                                border: '1px solid var(--border-primary, #d0d7de)',
                                borderRadius: '6px',
                                cursor: refireBusy ? 'not-allowed' : 'pointer',
                                fontSize: '0.85rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.4rem'
                            }}
                        >
                            <i className={`fas fa-bolt${refireBusy ? ' fa-spin' : ''}`}></i>
                            {refireBusy ? 'Re-firing…' : 'Re-fire Automations'}
                        </button>
                    )}
                    <button
                        className="btn-edit-project"
                        onClick={() => setShowProjectModal(true)}
                        style={{
                            padding: '0.4rem 1rem',
                            background: '#4a90d9',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '0.85rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.4rem'
                        }}
                    >
                        <i className="fas fa-edit"></i> Edit Project
                    </button>
                </div>
            </div>

            {saveError && (
                <div className="error-message" style={{ marginBottom: '12px' }}>
                    {saveError}
                </div>
            )}

            <div className="pd-layout">
                {/* Tab Sidebar */}
                <div className="pd-tab-sidebar">
                    {tabs.map(tab => (
                        <button
                            key={tab.id}
                            className={`pd-tab-btn${activeTab === tab.id ? ' active' : ''}`}
                            onClick={() => setActiveTab(tab.id)}
                        >
                            <i className={`fas ${tab.icon}`}></i>
                            {tab.label}
                            {tab.count !== undefined && (
                                <span className="pd-tab-count">{tab.count}</span>
                            )}
                        </button>
                    ))}
                </div>

                {/* Main Content */}
                <div className="pd-main-content">
                    {activeTab === 'activity' && (
                        <ActivityTab project={project} />
                    )}
                    {activeTab === 'tasks' && (
                        <TasksTab
                            tasks={tasks}
                            onRefresh={handleTaskRefresh}
                            onAddTask={() => { setEditingTask(null); setShowTaskModal(true); }}
                            onEditTask={(task) => { setEditingTask(task); setShowTaskModal(true); }}
                        />
                    )}
                    {activeTab === 'photos' && (
                        <PhotosTab
                            photos={photos}
                            projectId={projectId}
                            projectName={project.projectName}
                            onRefresh={handleDocRefresh}
                        />
                    )}
                    {activeTab === 'documents' && (
                        <DocumentsTab
                            documents={nonPhotoDocs}
                            projectId={projectId}
                            onRefresh={handleDocRefresh}
                        />
                    )}
                    {activeTab === 'workorders' && (
                        <WorkOrdersTab
                            project={project}
                            onCountChange={setWorkOrderCount}
                        />
                    )}
                    {activeTab === 'contacts' && (
                        <ProjectContacts projectId={projectId} />
                    )}
                    {activeTab === 'email' && (
                        <div className="email-tab-content">
                            <EmailComposer
                                project={project}
                                onEmailSent={() => setEmailRefreshTrigger(prev => prev + 1)}
                            />
                            <EmailHistory
                                projectId={projectId}
                                key={emailRefreshTrigger}
                            />
                        </div>
                    )}
                </div>

                {/* Details Drawer — fields save in place; no modal round-trip. */}
                <DetailsDrawer
                    project={project}
                    users={users}
                    crews={crews}
                    onSave={handleFieldSave}
                    onStatusChange={handleStatusChange}
                />
            </div>

            {/* Task Modal */}
            <TaskModal
                isOpen={showTaskModal}
                onClose={() => { setShowTaskModal(false); setEditingTask(null); }}
                onTaskCreated={handleTaskRefresh}
                editingTask={editingTask}
                defaultValues={taskDefaultValues}
            />

            {/* Project Edit Modal */}
            <ProjectModal
                isOpen={showProjectModal}
                onClose={() => setShowProjectModal(false)}
                onProjectCreated={() => { fetchProject(); fetchTasks(); }}
                editingProject={project}
                customers={customers}
                users={users}
            />
        </div>
    );
};

export default ProjectDetail;
